import { Component, type OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  BaseMatcherEditorComponent,
  type MatcherComponentRegistryService,
} from '@cdw-ae/filter-bar-core';
import type { SelectFromFilterOptions } from '@cdw-ae/filter-bar-interfaces';
import { SelectFromMatcher } from '@cdw-ae/filter-bar-matcher-configs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'cdw-select-from-matcher',
  template: `
    <div *ngIf="matcherOptions; else noOptions">
      <section cdkTrapFocus class="optionsList" [formGroup]="state">
        <div *ngFor="let option of listOptions">
          <lib-cdw-checkbox [formControlName]="option.name" [value]="option.name">{{ option.displayName }}</lib-cdw-checkbox>
        </div>
      </section>
    </div>
    <ng-template #noOptions><p>No options provided</p></ng-template>
  `,
  styles: [
    `
      lib-cdw-checkbox {
        width: 100%;
      }
    `,
  ],
  standalone: false,
})
export class SelectFromMatcherComponent
  extends BaseMatcherEditorComponent
  implements OnInit
{
  public listOptions!: {
    name: string;
    displayName: string;
    defaultTrue?: boolean;
  }[];

  public override state = new FormGroup({});

  constructor() {
    super();
    this.matcherDisplayName = SelectFromMatcher.displayName;
    this.matcherName = SelectFromMatcher.name;

    this.state.valueChanges.subscribe(
      (formState: { [key: string]: boolean }) => {
        this.valueToMatch = JSON.stringify(
          Object.entries(formState).reduce(
            (
              selectedOptions: string[],
              [optionName, optionValue]: [string, boolean]
            ) => {
              if (optionValue) {
                selectedOptions.push(optionName);
              }
              return selectedOptions;
            },
            []
          )
        );
      }
    );
  }

  ngOnInit(): void {
    const options: SelectFromFilterOptions = this.matcherOptions
      ?.selectedMatcher?.editorOptions as SelectFromFilterOptions;
    if (options) {
      this.listOptions = options.selectionOptions;

      // Parse existing matchValue to restore previously selected options
      let previouslySelected: string[] = [];
      if (this.matcherOptions?.matchValue) {
        try {
          previouslySelected = JSON.parse(this.matcherOptions.matchValue as string);
        } catch {
          previouslySelected = [];
        }
      }

      options.selectionOptions.forEach(
        (option: { name: string; displayName: string }) => {
          const isSelected = previouslySelected.includes(option.name);
          this.state.addControl(option.name, new FormControl<boolean>(isSelected));
        }
      );
      this.hasErrors$ = this.state.valueChanges.pipe(
        startWith(this.state.value as { [key: string]: boolean }),
        map((formState: { [key: string]: boolean }) => !Object.values(formState).some(v => v))
      );
    }
  }

  /**
   * Register this component with the matcher registry
   */
  public static registerSelectFrom(
    registry: MatcherComponentRegistryService
  ): void {
    registry.registerComponent('selectFrom', SelectFromMatcherComponent);
  }
}
